import {
    Box,
    Button,
    CircularProgress,
    InputAdornment,
    Link,
} from '@mui/material'
import UserIcon from '@mui/icons-material/Person'
import SecurePasswordIcon from '@mui/icons-material/Https'
import ShowPasswordIcon from '@mui/icons-material/Visibility'
import HidePasswordIcon from '@mui/icons-material/VisibilityOff'
import React, { FormEvent, useCallback, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { IconButton, TextField, FormHelperText } from './LoginForm.styled'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from './LoginForm.messages'
import ApiError from '../../entities/api-error/ApiError'
import { useOTAProgress } from '../../contexts/OTAProgressContext'
import OTAProgress from '../ota-progress/OTAProgress'
import NotificationGate from '../notification-gate/NotificationGate'
import {
    checkNotificationPermission,
    NotificationPermissionState,
} from '../../services/notifications/notificationPermission'

export interface IOwnProps {}

export interface IStateProps {
    isRequesting: boolean
    error?: ApiError
}

export interface IDispatchProps {
    requestLogin: (email: string, password: string) => void
}

export type IProps = IOwnProps & IStateProps & IDispatchProps

interface IField {
    value: string
}

const LoginForm: React.FC<IProps> = (props) => {
    const { requestLogin, isRequesting, error: apiError } = props
    const intl = useIntl()
    const { progress, isUpdating } = useOTAProgress()

    const [shouldShowPassword, setShouldShowPassword] = useState(false)
    const [email, setEmail] = useState<IField>({
        value: '',
    })
    const [password, setPassword] = useState<IField>({
        value: '',
    })
    // null = gate closed. Otherwise holds the permission state that opened it.
    const [gateState, setGateState] = useState<NotificationPermissionState | null>(
        null
    )

    const handleClickShowPassword = () => {
        setShouldShowPassword(!shouldShowPassword)
    }

    const handleMouseDownPassword = (
        event: React.MouseEvent<HTMLButtonElement>
    ) => {
        event.preventDefault()
    }

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmail({
            ...email,
            value: event.target.value,
        })
    }

    const handlePasswordChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setPassword({
            ...password,
            value: event.target.value,
        })
    }

    const proceedLogin = useCallback(() => {
        requestLogin(email.value, password.value)
    }, [requestLogin, email.value, password.value])

    // Permission became granted (via prompt or OS settings): close gate, log in.
    const handleNotificationsGranted = useCallback(() => {
        setGateState(null)
        proceedLogin()
    }, [proceedLogin])

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        // Block login if OTA update is in progress
        if (isUpdating) {
            return
        }

        // On native, notifications are mandatory: block login until the OS
        // permission is granted. Web is unaffected (gate is a no-op there).
        if (Capacitor.isNativePlatform()) {
            const permission = await checkNotificationPermission()
            if (permission !== 'granted') {
                setGateState(permission)
                return
            }
        }

        proceedLogin()
    }

    return (
        <>
        <form onSubmit={handleSubmit}>
            {/* Show OTA progress if update is in progress */}
            {progress && (
                <Box mb={3}>
                    <OTAProgress progress={progress} />
                </Box>
            )}
            
            <Box mb={4}>
                <TextField
                    type='email'
                    placeholder={intl.formatMessage({ ...messages.email })}
                    variant='outlined'
                    autoComplete='email'
                    value={email.value}
                    required
                    disabled={isUpdating}
                    onChange={handleEmailChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment
                                position='start'
                                disablePointerEvents
                            >
                                <UserIcon />
                            </InputAdornment>
                        ),
                    }}
                    autoFocus
                    fullWidth
                />
            </Box>

            <Box mb={'130px'}>
                <TextField
                    type={shouldShowPassword ? 'text' : 'password'}
                    placeholder={intl.formatMessage({ ...messages.password })}
                    variant='outlined'
                    autoComplete='current-password'
                    value={password.value}
                    required
                    disabled={isUpdating}
                    onChange={handlePasswordChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment
                                position='start'
                                disablePointerEvents
                            >
                                <SecurePasswordIcon />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position='end'>
                                <IconButton
                                    onClick={handleClickShowPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    edge='end'
                                >
                                    {shouldShowPassword ? (
                                        <HidePasswordIcon />
                                    ) : (
                                        <ShowPasswordIcon />
                                    )}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    fullWidth
                />
            </Box>

            {/* <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginBottom: '130px',

                    '@media (max-width: 768px)': {
                        marginBottom: '50px',
                    },
                }}
            >
                <Link
                    color='#7d7d7d'
                    fontSize='14px'
                    href={process.env.REACT_APP_EXTERNAL_LINK || '/'}
                >
                    {messages.forgotPasswordMessage.defaultMessage}
                </Link>
            </Box> */}

            <Box>
                <Button
                    type='submit'
                    color='primary'
                    variant='contained'
                    disabled={isRequesting || isUpdating}
                    fullWidth
                >
                    {isRequesting ? (
                        <CircularProgress size={24} />
                    ) : isUpdating ? (
                        'Updating App...'
                    ) : (
                        <FormattedMessage {...messages.logIn} />
                    )}
                </Button>

                {apiError ? (
                    <FormHelperText error>
                        <FormattedMessage {...messages.incorrectForm} />
                    </FormHelperText>
                ) : null}
            </Box>
        </form>

        <NotificationGate
            open={gateState !== null}
            state={gateState ?? 'prompt'}
            onClose={() => setGateState(null)}
            onGranted={handleNotificationsGranted}
        />
        </>
    )
}

export default LoginForm
